import { Component, DestroyRef, ElementRef, computed, inject, signal, viewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, concat, forkJoin, of } from 'rxjs';
import { switchMap, take, toArray } from 'rxjs/operators';
import {
  YdsButtonComponent,
  YdsButtonBackComponent,
  YdsCardComponent,
  YdsCardEditableComponent,
  YdsDatePickerComponent,
  YdsDropdownComponent,
  YdsSpinnerComponent,
  YdsSwitchComponent,
  YdsTextAreaComponent,
  YdsTextFieldComponent,
  type YdsDropdownOption,
} from '../../../yds-angular';
import {
  defaultEndDate,
  defaultStartDate,
  formatDisplayDate,
  isInvalid,
  startBeforeEndValidator,
  toFormDate,
} from '../../../shared/general-ledger-yds.utils';
import { ExportDefinition } from '../../../model/export-definition.model';
import { ExportRule, SelectionCodeOption } from '../../../model/export-rule.model';
import { ExportCondenseCriterion } from '../../../model/export-condense-criterion.model';
import { ExportDefinitionService } from '../export-definition.service';
import { ExportRuleService } from '../export-rule.service';
import { ExportCondenseCriterionService } from '../export-condense-criterion.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import { deleteExportDefinitionWithChecks, exportLineLabel } from '../exportdefinition-shared';
import { ExportruleDialogComponent } from '../exportrule-dialog/exportrule-dialog.component';

const MAX_END_DATE_ISO = '9999-12-31';

/** Number of numbered Verdichtingscriteria (Criterium 1 - 8). */
const MAX_CRITERIA = 8;

/**
 * Reserved negative criterion numbers. They have no 'Export_CondenseCriterion'
 * record: the backend maps them onto bit fields of 'Export_Definition', but they
 * are set (POST) and reset (DELETE) through the ExportCondenseCriterion API.
 */
const CONDENSE_EMPLOYMENT = -1;
const CONDENSE_OUTPUT_CODE = -2;
const CONDENSE_BOOKING_DESCRIPTION = -3;

interface CriterionSlot {
  number: number;
  label: string;
}

/** Rows of the Verdichtingscriteria card, in display order. */
const CRITERION_SLOTS: readonly CriterionSlot[] = [
  ...Array.from({ length: MAX_CRITERIA }, (_, index) => ({
    number: index + 1,
    label: `Criterium ${index + 1}`,
  })),
  { number: CONDENSE_EMPLOYMENT, label: 'Medewerker' },
  { number: CONDENSE_OUTPUT_CODE, label: 'Uitvoercodes' },
  { number: CONDENSE_BOOKING_DESCRIPTION, label: 'Boekingsomschrijving' },
];

/** Coded-field defaults for a new definition (match the backend DTO defaults). */
const DEFAULT_CODES = {
  itemSeparatorCode: 'TAB',
  lineSeparatorCode: 'CRLF',
  endOfFileCode: '-',
  exportFileExtensionCode: 'XLSX',
  showZero: 'ALL',
} as const;

/** Selection code 'Anders', which reveals the free-text character field below the dropdown. */
const OTHER_CODE = 'OTHER';

const MAX_CHARACTER_LENGTH = 50;

/** Dropdowns that reveal a free-text character field when set to {@link OTHER_CODE}. */
const CHARACTER_FIELDS = [
  { codeControl: 'itemSeparatorCode', characterControl: 'itemSeparatorCharacter' },
  { codeControl: 'lineSeparatorCode', characterControl: 'lineSeparatorCharacter' },
  { codeControl: 'endOfFileCode', characterControl: 'endOfFileCharacter' },
] as const;

type CharacterControl = (typeof CHARACTER_FIELDS)[number]['characterControl'];

interface RuleSection {
  code: string;
  label: string;
  rule: ExportRule | null;
}
 
@Component({
  selector: 'app-exportdefinition-editable',
  imports: [
    ReactiveFormsModule,
    YdsCardComponent,
    YdsCardEditableComponent,
    YdsButtonComponent,
    YdsButtonBackComponent,
    YdsSpinnerComponent,
    YdsSwitchComponent,
    YdsDatePickerComponent,
    YdsDropdownComponent,
    YdsTextAreaComponent,
    YdsTextFieldComponent,
    ExportruleDialogComponent,
  ],
  templateUrl: './exportdefinition-editable.component.html',
  styleUrl: './exportdefinition-editable.component.scss',
})
export class ExportdefinitionEditableComponent {
  readonly isInvalid = isInvalid;
  readonly translate = inject(TranslateService);

  private formBuilder = inject(FormBuilder);
  private exportDefinitionService = inject(ExportDefinitionService);
  private exportRuleService = inject(ExportRuleService);
  private exportCondenseCriterionService = inject(ExportCondenseCriterionService);
  private dialogService = inject(ConfirmDialogService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private document = inject(DOCUMENT);

  private readonly detailsCard = viewChild('detailsCard', { read: ElementRef<HTMLElement> });

  readonly exportDefinitionId = signal<string | null>(null);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly notFound = signal(false);

  /** Last loaded/saved record, used for read-only views and to revert on cancel. */
  private readonly record = signal<ExportDefinition | null>(null);

  readonly detailsEditing = signal(false);

  readonly form: FormGroup = this.formBuilder.group(
    {
      exportDefinitionName: ['', Validators.required],
      itemSeparatorCode: [DEFAULT_CODES.itemSeparatorCode, Validators.required],
      itemSeparatorCharacter: [null as string | null, Validators.maxLength(MAX_CHARACTER_LENGTH)],
      lineSeparatorCode: [DEFAULT_CODES.lineSeparatorCode, Validators.required],
      lineSeparatorCharacter: [null as string | null, Validators.maxLength(MAX_CHARACTER_LENGTH)],
      endOfFileCode: [DEFAULT_CODES.endOfFileCode, Validators.required],
      endOfFileCharacter: [null as string | null, Validators.maxLength(MAX_CHARACTER_LENGTH)],
      exportFileExtensionCode: [DEFAULT_CODES.exportFileExtensionCode, Validators.required],
      showZero: [DEFAULT_CODES.showZero, Validators.required],
      removeLeadingAndTrailingSpaces: [true],
      splitBookingOnDebitCredit: [false],
      applyAccountDescriptionIndicator: [true],
      applyQuotesForNotApplicable: [false],
      automaticJournaling: [true],
      startDate: [defaultStartDate(), Validators.required],
      endDate: [null],
      memoValue: [''],
    },
    { validators: startBeforeEndValidator() },
  );

  readonly isAddMode = computed(() => this.exportDefinitionId() === null);

  readonly exportDefinitionName = computed(() => this.record()?.exportDefinitionName ?? '');

  // --- Details coded dropdowns (SystemCode selection lists) ---------------------

  readonly itemSeparatorOptions = signal<SelectionCodeOption[]>([]);
  readonly lineSeparatorOptions = signal<SelectionCodeOption[]>([]);
  readonly endOfFileOptions = signal<SelectionCodeOption[]>([]);
  readonly exportFileExtensionOptions = signal<SelectionCodeOption[]>([]);
  readonly showZeroOptions = signal<SelectionCodeOption[]>([]);

  readonly itemSeparatorDropdownOptions = computed(() =>
    this.toDropdownOptions(
      this.itemSeparatorOptions(),
      this.record()?.itemSeparatorCode ?? DEFAULT_CODES.itemSeparatorCode,
    ),
  );
  readonly lineSeparatorDropdownOptions = computed(() =>
    this.toDropdownOptions(
      this.lineSeparatorOptions(),
      this.record()?.lineSeparatorCode ?? DEFAULT_CODES.lineSeparatorCode,
    ),
  );
  readonly endOfFileDropdownOptions = computed(() =>
    this.toDropdownOptions(
      this.endOfFileOptions(),
      this.record()?.endOfFileCode ?? DEFAULT_CODES.endOfFileCode,
    ),
  );
  readonly exportFileExtensionDropdownOptions = computed(() =>
    this.toDropdownOptions(
      this.exportFileExtensionOptions(),
      this.record()?.exportFileExtensionCode ?? DEFAULT_CODES.exportFileExtensionCode,
    ),
  );
  readonly showZeroDropdownOptions = computed(() =>
    this.toDropdownOptions(
      this.showZeroOptions(),
      this.record()?.showZero ?? DEFAULT_CODES.showZero,
    ),
  );

  // --- Free-text character fields ('Anders') -----------------------------------

  private readonly characterCodes = signal<Record<string, string | null>>({});

  /** Whether each character field is shown, keyed by its control name. */
  readonly showCharacterField = computed(() => {
    const codes = this.characterCodes();
    return Object.fromEntries(
      CHARACTER_FIELDS.map((field) => [
        field.characterControl,
        codes[field.codeControl] === OTHER_CODE,
      ]),
    ) as Record<CharacterControl, boolean>;
  });

  /** Public view of the loaded record for the read-only Details rows. */
  readonly detail = computed(() => this.record());

  /** Boolean settings rendered in the read-only Details view. */
  readonly booleanSettings: readonly { label: string; value: () => boolean }[] = [
    {
      label: 'Rubrieken aansluitend exporteren',
      value: () => this.record()?.removeLeadingAndTrailingSpaces ?? false,
    },
    {
      label: 'Boeking met D en C splitsen',
      value: () => this.record()?.splitBookingOnDebitCredit ?? false,
    },
    {
      label: 'Toepassen rekening omschrijving',
      value: () => this.record()?.applyAccountDescriptionIndicator ?? false,
    },
    {
      label: 'Toepassen quotes bij n.v.t. waarde',
      value: () => this.record()?.applyQuotesForNotApplicable ?? false,
    },
    {
      label: 'Automatisch journaliseren',
      value: () => this.record()?.automaticJournaling ?? false,
    },
  ];

  // --- Verdichtingscriteria card state -----------------------------------------

  readonly criteriaEditing = signal(false);
  readonly isSavingCriteria = signal(false);

  /** Rows shown on the card: Criterium 1 - 8, then the three reserved criteria. */
  readonly criterionSlots = CRITERION_SLOTS;

  /** Criteria as persisted in the backend; the save diff is computed against this. */
  private readonly savedCriteria = signal<ExportCondenseCriterion[]>([]);

  /** Numbers toggled on (working copy while editing, equal to the saved set otherwise). */
  private readonly selectedCriterionNumbers = signal<ReadonlySet<number>>(new Set());

  // --- Exportregel card state ---------------------------------------------------

  readonly rules = signal<ExportRule[]>([]);
  readonly kindOptions = signal<SelectionCodeOption[]>([]);
  readonly headerLevelOptions = signal<SelectionCodeOption[]>([]);

  readonly dialogOpen = signal(false);
  readonly dialogEditRule = signal<ExportRule | null>(null);
  readonly dialogPresetKind = signal('');

  readonly existingKinds = computed(() => this.rules().map((rule) => rule.kindOfExportLine));

  /** One section per line kind (from the selection list, or derived from rules). */
  readonly ruleSections = computed<RuleSection[]>(() => {
    const options = this.kindOptions();
    const rules = this.rules();
    const codes =
      options.length > 0
        ? options.map((option) => option.code)
        : Array.from(new Set(rules.map((rule) => rule.kindOfExportLine)));
    return codes.map((code) => ({
      code,
      label: exportLineLabel(code, options.find((o) => o.code === code)?.description),
      rule: rules.find((rule) => rule.kindOfExportLine === code) ?? null,
    }));
  });

  constructor() {
    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.exportDefinitionId.set(id);
      this.loadDefinition(id);
      this.loadCriteria(id);
      this.loadRules(id);
    } else {
      this.detailsEditing.set(true);
    }

    for (const field of CHARACTER_FIELDS) {
      this.form
        .get(field.codeControl)
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.refreshCharacterFields());
    }
    this.refreshCharacterFields();

    // The selection lists power both the rule sections and the popup.
    this.loadSelectionLists();
  }

  private loadDefinition(id: string): void {
    this.isLoading.set(true);
    this.exportDefinitionService
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (definition) => {
          if (!definition) {
            this.notFound.set(true);
          } else {
            this.record.set(definition);
            this.patchForms(definition);
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.notFound.set(true);
          this.isLoading.set(false);
        },
      });
  }

  private loadCriteria(id: string): void {
    this.exportCondenseCriterionService
      .getByExportDefinition(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rows) => {
        this.savedCriteria.set(rows);
        this.resetCriterionSelection();
      });
  }

  private loadRules(id: string): void {
    this.exportRuleService
      .getByExportDefinition(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rules) => this.rules.set(rules));
  }

  private loadSelectionLists(): void {
    this.exportRuleService
      .getKindOfExportLineOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => this.kindOptions.set(options));

    this.exportRuleService
      .getHeaderLevelOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => this.headerLevelOptions.set(options));

    // Details card dropdowns.
    this.exportDefinitionService
      .getItemSeparatorCodeOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => this.itemSeparatorOptions.set(options));

    this.exportDefinitionService
      .getLineSeparatorCodeOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => this.lineSeparatorOptions.set(options));

    this.exportDefinitionService
      .getEndOfFileCodeOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => this.endOfFileOptions.set(options));

    this.exportDefinitionService
      .getExportFileExtensionCodeOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => this.exportFileExtensionOptions.set(options));

    this.exportDefinitionService
      .getShowZeroOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => this.showZeroOptions.set(options));
  }

  private patchForms(definition: ExportDefinition): void {
    this.form.patchValue({
      exportDefinitionName: definition.exportDefinitionName,
      itemSeparatorCode: definition.itemSeparatorCode,
      lineSeparatorCode: definition.lineSeparatorCode,
      endOfFileCode: definition.endOfFileCode || '-',
      itemSeparatorCharacter: definition.itemSeparatorCharacter,
      lineSeparatorCharacter: definition.lineSeparatorCharacter,
      endOfFileCharacter: definition.endOfFileCharacter,
      exportFileExtensionCode: definition.exportFileExtensionCode,
      showZero: definition.showZero,
      removeLeadingAndTrailingSpaces: definition.removeLeadingAndTrailingSpaces,
      splitBookingOnDebitCredit: definition.splitBookingOnDebitCredit,
      applyAccountDescriptionIndicator: definition.applyAccountDescriptionIndicator,
      applyQuotesForNotApplicable: definition.applyQuotesForNotApplicable,
      automaticJournaling: definition.automaticJournaling,
      startDate: toFormDate(definition.startDate),
      endDate: this.isMaxEndDate(definition.endDate) ? null : toFormDate(definition.endDate),
      memoValue: definition.memoValue ?? '',
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.refreshCharacterFields();
  }

  /** Re-reads the three dropdowns and aligns their character controls. */
  private refreshCharacterFields(): void {
    this.characterCodes.set(
      Object.fromEntries(
        CHARACTER_FIELDS.map((field) => [
          field.codeControl,
          (this.form.get(field.codeControl)?.value as string | null) ?? null,
        ]),
      ),
    );

    const visible = this.showCharacterField();
    for (const field of CHARACTER_FIELDS) {
      const control = this.form.get(field.characterControl);
      if (visible[field.characterControl]) {
        control?.setValidators([
          Validators.required,
          Validators.maxLength(MAX_CHARACTER_LENGTH),
        ]);
        control?.enable({ emitEvent: false });
      } else {
        control?.setValue(null, { emitEvent: false });
        control?.setValidators(Validators.maxLength(MAX_CHARACTER_LENGTH));
        control?.disable({ emitEvent: false });
      }
      control?.updateValueAndValidity({ emitEvent: false });
    }
  }

  // --- View helpers -------------------------------------------------------------

  readonly startDateDisplay = computed(() => formatDisplayDate(this.record()?.startDate, 'nl-NL'));

  readonly endDateDisplay = computed(() => {
    const end = this.record()?.endDate;
    return this.isMaxEndDate(end) ? 'N.v.t.' : formatDisplayDate(end, 'nl-NL');
  });

  readonly memoDisplay = computed(() => this.record()?.memoValue || 'N.v.t.');

  /** Human-readable display for a coded value ('-' / empty become 'N.v.t.'). */
  codeDisplay(value: string | null | undefined): string {
    const trimmed = (value ?? '').trim();
    return trimmed && trimmed !== '-' ? trimmed : 'N.v.t.';
  }

  /** True when a dropdown holds 'Anders', so its character field applies. */
  isOtherCode(code: string | null | undefined): boolean {
    return code === OTHER_CODE;
  }

  /** Character control value, with blanks collapsed to null for the payload. */
  private characterValue(control: CharacterControl): string | null {
    const value = this.form.get(control)?.value as string | null | undefined;
    return value?.trim() ? value : null;
  }

  /** Description of a code from its selection list, falling back to the code itself. */
  selectionLabel(options: SelectionCodeOption[], code: string | null | undefined): string {
    const match = options.find((option) => option.code === code);
    if (match) {
      return match.description || match.code;
    }
    return this.codeDisplay(code);
  }

  /** Dutch label for a header-level code, from the selection list. */
  headerLevelLabel(code: string | null | undefined): string {
    return this.selectionLabel(this.headerLevelOptions(), code);
  }

  /**
   * Maps a selection list to dropdown options. A saved code that is missing from
   * the list is kept as an extra option, so the dropdown never shows a blank
   * value while still holding a code.
   */
  private toDropdownOptions(
    options: SelectionCodeOption[],
    currentCode: string | null | undefined,
  ): YdsDropdownOption[] {
    const items: YdsDropdownOption[] = options.map((option) => ({
      id: option.code,
      label: option.description || option.code,
    }));
    if (currentCode && !items.some((item) => item.id === currentCode)) {
      items.push({ id: currentCode, label: currentCode });
    }
    return items;
  }

  private isMaxEndDate(value: unknown): boolean {
    const date = toFormDate(value as Date | string | null);
    if (!date) {
      return false;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}` === MAX_END_DATE_ISO;
  }

  // --- Details card actions -----------------------------------------------------

  onDetailsCancel(): void {
    if (this.isAddMode()) {
      this.goBack();
      return;
    }

    const current = this.record();
    if (current) {
      this.patchForms(current);
    }
    this.detailsEditing.set(false);
    this.scrollToDetails();
  }

  onDetailsNext(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.buildDefinitionFromDetails();
    this.isSaving.set(true);

    const id = this.exportDefinitionId();
    const save$ = id
      ? this.exportDefinitionService.updateExportDefinition(id, payload)
      : this.exportDefinitionService.addExportDefinition(payload);

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => {
        this.isSaving.set(false);
        if (id) {
          this.record.set({ ...payload, ...saved });
          this.detailsEditing.set(false);
          this.scrollToDetails();
        } else if (saved?.exportDefinitionId) {
          this.router.navigate(['/config/exportdefinition/editable', saved.exportDefinitionId]);
        } else {
          this.router.navigate(['/config/exportdefinition']);
        }
      },
      error: () => this.isSaving.set(false),
    });
  }

  /**
   * Combines the Details form with the current record. The `Condense_*` flags are
   * required by the DTO but not edited by this card: Employment, OutputCode and
   * BookingDescription are the reserved Verdichtingscriteria -1 .. -3 (kept in sync
   * by {@link syncReservedCondenseFlags}); CostCenter and CostOwner are not shown.
   */
  private buildDefinitionFromDetails(): ExportDefinition {
    const value = this.form.value as Record<string, unknown>;
    const current = this.record();
    return {
      exportDefinitionId: this.exportDefinitionId() ?? '',
      exportDefinitionName: (value['exportDefinitionName'] as string) ?? '',
      payrollClientId: null,
      institutionId: null,
      condenseCostCenter: current?.condenseCostCenter ?? false,
      condenseCostOwner: current?.condenseCostOwner ?? false,
      condenseEmployment: current?.condenseEmployment ?? false,
      condenseOutputCode: current?.condenseOutputCode ?? false,
      condenseBookingDescription: current?.condenseBookingDescription ?? false,
      itemSeparatorCode: (value['itemSeparatorCode'] as string) ?? '',
      itemSeparatorCharacter: this.characterValue('itemSeparatorCharacter'),
      lineSeparatorCode: (value['lineSeparatorCode'] as string) ?? '',
      lineSeparatorCharacter: this.characterValue('lineSeparatorCharacter'),
      endOfFileCode: ((value['endOfFileCode'] as string) || '-').trim() || '-',
      endOfFileCharacter: this.characterValue('endOfFileCharacter'),
      exportFileExtensionCode: (value['exportFileExtensionCode'] as string) ?? '',
      showZero: (value['showZero'] as string) ?? '',
      startDate: (value['startDate'] as Date | null) ?? defaultStartDate(),
      endDate: (value['endDate'] as Date | null) ?? defaultEndDate(),
      memoValue: (value['memoValue'] as string) ?? '',
      removeLeadingAndTrailingSpaces: Boolean(value['removeLeadingAndTrailingSpaces']),
      splitBookingOnDebitCredit: Boolean(value['splitBookingOnDebitCredit']),
      applyAccountDescriptionIndicator: Boolean(value['applyAccountDescriptionIndicator']),
      applyQuotesForNotApplicable: Boolean(value['applyQuotesForNotApplicable']),
      automaticJournaling: Boolean(value['automaticJournaling']),
    };
  }

  // --- Verdichtingscriteria card actions ----------------------------------------

  isCriterionSelected(criterionNumber: number): boolean {
    return this.selectedCriterionNumbers().has(criterionNumber);
  }

  toggleCriterion(criterionNumber: number, selected: boolean): void {
    this.selectedCriterionNumbers.update((current) => {
      const next = new Set(current);
      if (selected) {
        next.add(criterionNumber);
      } else {
        next.delete(criterionNumber);
      }
      return next;
    });
  }

  /** Aligns the toggles with what is persisted in the backend. */
  private resetCriterionSelection(): void {
    this.selectedCriterionNumbers.set(
      new Set(this.savedCriteria().map((criterion) => criterion.number)),
    );
  }

  onCriteriaCancel(): void {
    this.resetCriterionSelection();
    this.criteriaEditing.set(false);
  }

  onCriteriaSave(): void {
    const id = this.exportDefinitionId();
    if (!id) {
      return;
    }

    const saved = this.savedCriteria();
    const selected = this.selectedCriterionNumbers();
    const savedNumbers = new Set(saved.map((criterion) => criterion.number));

    const slotNumbers = new Set(this.criterionSlots.map((slot) => slot.number));

    // One POST per toggle switched on, each carrying its own number
    // (e.g. switching on 1, 3, 5 and 8 results in four calls). The reserved
    // numbers -1 .. -3 are posted the same way; the backend sets the bit.
    const addOperations = this.criterionSlots
      .filter((slot) => selected.has(slot.number) && !savedNumbers.has(slot.number))
      .map((slot) =>
        this.exportCondenseCriterionService.addCriterion({
          exportCondenseCriterionId: null,
          exportDefinitionId: id,
          number: slot.number,
        }),
      );

    // One DELETE per toggle switched off (for -1 .. -3 the backend resets the bit).
    // Numbers that are not shown on the card are left untouched.
    const deleteOperations = saved
      .filter(
        (criterion) =>
          criterion.exportCondenseCriterionId !== null &&
          slotNumbers.has(criterion.number) &&
          !selected.has(criterion.number),
      )
      .map((criterion) =>
        this.exportCondenseCriterionService.removeCriterion(
          criterion.exportCondenseCriterionId as string,
        ),
      );

    this.isSavingCriteria.set(true);

    this.runInOrder(deleteOperations, addOperations)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSavingCriteria.set(false);
          this.criteriaEditing.set(false);
          this.syncReservedCondenseFlags(selected);
          this.loadCriteria(id);
        },
        error: () => this.isSavingCriteria.set(false),
      });
  }

  /**
   * Mirrors the reserved criteria into the loaded record. The Details card PUTs
   * the whole definition including these bits, so without this a Details save
   * after a criteria save would send the stale values and reset the bits again.
   */
  private syncReservedCondenseFlags(selected: ReadonlySet<number>): void {
    this.record.update((current) =>
      current
        ? {
            ...current,
            condenseEmployment: selected.has(CONDENSE_EMPLOYMENT),
            condenseOutputCode: selected.has(CONDENSE_OUTPUT_CODE),
            condenseBookingDescription: selected.has(CONDENSE_BOOKING_DESCRIPTION),
          }
        : current,
    );
  }

  /**
   * Runs the given operation groups sequentially (all deletes first, then all
   * adds). Operations within a group run in parallel. Emits exactly once, after
   * every group has completed.
   */
  private runInOrder(...groups: Observable<unknown>[][]): Observable<unknown> {
    const phases = groups.filter((group) => group.length > 0).map((group) => forkJoin(group));
    return phases.length ? concat(...phases).pipe(toArray()) : of(null);
  }

  // --- Exportregel card actions -------------------------------------------------

  openAddRule(presetKind = ''): void {
    this.dialogEditRule.set(null);
    this.dialogPresetKind.set(presetKind);
    this.dialogOpen.set(true);
  }

  openEditRule(rule: ExportRule): void {
    this.dialogEditRule.set(rule);
    this.dialogPresetKind.set('');
    this.dialogOpen.set(true);
  }

  onRuleSaved(): void {
    const id = this.exportDefinitionId();
    if (id) {
      this.loadRules(id);
    }
  }

  removeRule(rule: ExportRule): void {
    this.dialogService.open({
      width: '320px',
      title: 'Verwijderen?',
      message: `Exportregel '${rule.exportRuleName}' verwijderen?`,
      cancelText: 'Nee',
      confirmText: 'Ja',
      variant: 'error',
    });

    this.dialogService
      .confirmed()
      .pipe(
        take(1),
        switchMap((confirmed) =>
          confirmed ? this.exportRuleService.removeExportRule(rule.exportRuleId) : EMPTY,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.onRuleSaved(),
        error: () => undefined,
      });
  }

  // --- Page actions -------------------------------------------------------------

  private scrollToDetails(): void {
    setTimeout(() => {
      const element = this.detailsCard()?.nativeElement;
      if (!element) {
        return;
      }
      const chrome = this.document.querySelector('.app-chrome') as HTMLElement | null;
      const offset = chrome?.offsetHeight ?? 0;
      const top = element.getBoundingClientRect().top + window.scrollY - offset - 16;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  goBack(): void {
    this.router.navigateByUrl('config/exportdefinition');
  }

  onDelete(): void {
    const id = this.exportDefinitionId();
    if (!id) {
      return;
    }

    deleteExportDefinitionWithChecks({
      id,
      exportDefinitionName: this.exportDefinitionName(),
      service: this.exportDefinitionService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.router.navigateByUrl('config/exportdefinition'),
    });
  }
}
